import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { showModal, hideModal, installModalFallback } from "../Ged/Modalhelper";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./Projets.css";

const baseUrl = process.env.REACT_APP_BASE_URL;

const API = {
    projets: `${baseUrl}/api/Projets`,
    projet: (id) => `${baseUrl}/api/Projets/${id}`,
    projetDocuments: (id) => `${baseUrl}/api/Projets/${id}/documents`,
    clients: `${baseUrl}/api/Clients`,
    preview: (id) => `${baseUrl}/api/ged/documents/${id}/preview`,
    download: (id) => `${baseUrl}/api/ged/documents/${id}/download`,
};

// Enum ProjetStatut de l'API : EnCours = 1, Termine = 2 (valeur ou nom)
const STATUTS = [
    { value: 1, name: "EnCours", label: "En cours", css: "badge-warning" },
    { value: 2, name: "Termine", label: "Terminé", css: "badge-success" },
];
const findStatut = (s) => STATUTS.find((x) => x.value === Number(s) || x.name === s) || STATUTS[0];

const unwrap = (res) => (res.data && res.data.data !== undefined ? res.data.data : res.data);
const apiMessage = (error, fallback) => error.response?.data?.message || error.response?.data?.Message || fallback;
const toast = (icon, title) =>
    Swal.fire({ toast: true, position: "top-end", icon, title, showConfirmButton: false, timer: 2000 });

const formatDate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "—");
const toDateInput = (v) => (v ? String(v).substring(0, 10) : "");
const formatAmount = (v) =>
    v === null || v === undefined || v === "" ? "—"
        : Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " DH";

const mediaKind = (d) => {
    const ct = (d.contentType || "").toLowerCase();
    const ext = (d.fileName || "").split(".").pop().toLowerCase();
    if (ct.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) return "image";
    if (ct.startsWith("video/") || ["mp4", "webm", "mov", "avi", "mkv"].includes(ext)) return "video";
    return null;
};

const initialValues = { nom: "", clientId: "", lieu: "", dateDebut: "", dateFin: "", budget: "", description: "", statut: 1 };

const validationSchema = Yup.object({
    nom: Yup.string().trim().required("Le nom est obligatoire").max(150, "150 caractères maximum"),
    lieu: Yup.string().nullable().max(200, "200 caractères maximum"),
    dateFin: Yup.string().nullable().test("apres-debut", "La date de fin doit suivre la date de début", function (v) {
        const { dateDebut } = this.parent;
        return !v || !dateDebut || v >= dateDebut;
    }),
    budget: Yup.number().typeError("Budget invalide").min(0, "Budget invalide").nullable()
        .transform((v, orig) => (orig === "" ? null : v)),
});

const renderField = (formik, name, label, props = {}) => (
    <>
        <label>{label}</label>
        <input
            name={name}
            className={`form-control ${formik.touched[name] && formik.errors[name] ? "is-invalid" : ""}`}
            value={formik.values[name] ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            {...props}
        />
        <div className="invalid-feedback">{formik.errors[name]}</div>
    </>
);

const Projets = () => {
    const [projets, setProjets] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);           // null = création

    const [search, setSearch] = useState("");
    const [statutFilter, setStatutFilter] = useState("");

    // Galerie
    const [galleryProjet, setGalleryProjet] = useState(null);
    const [galleryDocs, setGalleryDocs] = useState([]);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [thumbs, setThumbs] = useState({});
    const thumbsRef = useRef({});
    const [lightbox, setLightbox] = useState(null);         // { doc, kind, url, owned, loading }

    useEffect(() => {
        fetchProjets();
        axiosInstance.get(API.clients)
            .then((res) => setClients((unwrap(res) || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
            .catch(() => setClients([]));
    }, []);

    useEffect(() => installModalFallback(), []);
    useEffect(() => () => clearThumbs(), []);

    const fetchProjets = () => {
        axiosInstance.get(API.projets)
            .then((res) => setProjets(unwrap(res) || []))
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger les projets")))
            .finally(() => setLoading(false));
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return projets.filter((p) => {
            if (statutFilter && findStatut(p.statut).value !== Number(statutFilter)) return false;
            if (!term) return true;
            return [p.nom, p.clientName, p.lieu, p.description].filter(Boolean).join(" ").toLowerCase().includes(term);
        });
    }, [projets, search, statutFilter]);

    // ---------- CRUD ----------
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { setErrors }) => {
            const payload = {
                nom: values.nom.trim(),
                clientId: values.clientId ? Number(values.clientId) : null,
                lieu: values.lieu?.trim() || null,
                dateDebut: values.dateDebut || null,
                dateFin: values.dateFin || null,
                budget: values.budget === "" || values.budget === null ? null : parseFloat(values.budget),
                description: values.description?.trim() || null,
                statut: Number(values.statut),
                coverDocumentId: editing?.coverDocumentId ?? null,
            };
            try {
                if (editing) await axiosInstance.put(API.projet(editing.id), payload);
                else await axiosInstance.post(API.projets, payload);
                hideModal("projetModal");
                toast("success", editing ? "Projet modifié" : "Projet créé");
                fetchProjets();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    const openCreate = () => {
        setEditing(null);
        formik.resetForm({ values: initialValues });
        showModal("projetModal");
    };

    const openEdit = (p) => {
        setEditing(p);
        formik.resetForm({
            values: {
                nom: p.nom || "",
                clientId: p.clientId ?? "",
                lieu: p.lieu || "",
                dateDebut: toDateInput(p.dateDebut),
                dateFin: toDateInput(p.dateFin),
                budget: p.budget ?? "",
                description: p.description || "",
                statut: findStatut(p.statut).value,
            },
        });
        showModal("projetModal");
    };

    const handleDelete = (p) => {
        Swal.fire({
            title: `Supprimer le projet « ${p.nom} » ?`,
            text: p.nbDocuments ? "Ce projet a des documents : retirez-les d'abord." : "Cette action est définitive.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await axiosInstance.delete(API.projet(p.id));
                toast("success", "Projet supprimé");
                fetchProjets();
            } catch (error) {
                Swal.fire({ icon: "error", title: "Suppression impossible", text: apiMessage(error, "Opération échouée") });
            }
        });
    };

    // ---------- Galerie ----------
    const clearThumbs = () => {
        Object.values(thumbsRef.current).forEach((u) => window.URL.revokeObjectURL(u));
        thumbsRef.current = {};
        setThumbs({});
    };

    const loadThumbs = (docs) => {
        docs.filter((d) => mediaKind(d) === "image").forEach(async (d) => {
            try {
                const res = await axiosInstance.get(API.preview(d.id), { responseType: "blob" });
                thumbsRef.current = { ...thumbsRef.current, [d.id]: window.URL.createObjectURL(res.data) };
                setThumbs({ ...thumbsRef.current });
            } catch { /* miniature indisponible : icône */ }
        });
    };

    const openGallery = async (p) => {
        clearThumbs();
        setGalleryProjet(p);
        setGalleryDocs([]);
        setGalleryLoading(true);
        showModal("galleryModal");
        try {
            const res = await axiosInstance.get(API.projetDocuments(p.id));
            const docs = unwrap(res) || [];
            setGalleryDocs(docs);
            loadThumbs(docs);
        } catch (error) {
            toast("error", apiMessage(error, "Impossible de charger les documents du projet"));
        } finally {
            setGalleryLoading(false);
        }
    };

    const mediaDocs = useMemo(() => galleryDocs.filter((d) => mediaKind(d)), [galleryDocs]);
    const otherDocs = useMemo(() => galleryDocs.filter((d) => !mediaKind(d)), [galleryDocs]);

    const setCover = async (doc) => {
        const p = galleryProjet;
        try {
            await axiosInstance.put(API.projet(p.id), {
                nom: p.nom, clientId: p.clientId ?? null, lieu: p.lieu ?? null,
                dateDebut: p.dateDebut ?? null, dateFin: p.dateFin ?? null, budget: p.budget ?? null,
                description: p.description ?? null, statut: findStatut(p.statut).value,
                coverDocumentId: doc.id,
            });
            setGalleryProjet({ ...p, coverDocumentId: doc.id });
            toast("success", "Photo de couverture définie");
            fetchProjets();
        } catch (error) {
            toast("error", apiMessage(error, "Opération échouée"));
        }
    };

    const handleDownload = async (doc) => {
        try {
            const res = await axiosInstance.get(API.download(doc.id), { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = doc.fileName || "document";
            link.click();
            window.URL.revokeObjectURL(url);
        } catch {
            toast("error", "Téléchargement échoué");
        }
    };

    const openLightbox = async (doc) => {
        const kind = mediaKind(doc);
        if (kind === "image" && thumbs[doc.id]) {
            setLightbox({ doc, kind, url: thumbs[doc.id], owned: false });
            return;
        }
        setLightbox({ doc, kind, url: null, loading: true });
        try {
            const res = await axiosInstance.get(API.preview(doc.id), { responseType: "blob" });
            setLightbox({ doc, kind, url: window.URL.createObjectURL(res.data), owned: true });
        } catch {
            setLightbox(null);
            toast("error", "Aperçu indisponible");
        }
    };

    const closeLightbox = () => {
        if (lightbox?.owned && lightbox.url) window.URL.revokeObjectURL(lightbox.url);
        setLightbox(null);
    };

    const moveLightbox = (step) => {
        if (!lightbox || mediaDocs.length < 2) return;
        const i = mediaDocs.findIndex((d) => d.id === lightbox.doc.id);
        if (lightbox.owned && lightbox.url) window.URL.revokeObjectURL(lightbox.url);
        openLightbox(mediaDocs[(i + step + mediaDocs.length) % mediaDocs.length]);
    };

    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            if (e.key === "ArrowRight") moveLightbox(1);
            if (e.key === "ArrowLeft") moveLightbox(-1);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [lightbox, mediaDocs]);

    // ---------- Rendu ----------
    return (
        <div className="container-fluid mt-4">
            <h3 className="text-center my-3">Projets / Réalisations</h3>

            <div className="d-flex flex-wrap align-items-center mb-3">
                <input
                    type="text"
                    className="form-control mr-2 mb-2 projets-search"
                    placeholder="Rechercher (nom, client, lieu…)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select className="form-control mr-2 mb-2 projets-filter" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
                    <option value="">Tous les statuts</option>
                    {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <button className="btn btn-primary btn-ajouter ml-auto mb-2" onClick={openCreate}>
                    <i className="fas fa-plus"></i> Nouveau projet
                </button>
            </div>

            <div className="table-responsive">
                <table className="table table-hover table-sm bg-white">
                    <thead className="thead-light">
                        <tr>
                            <th>Nom</th>
                            <th>Client</th>
                            <th>Lieu</th>
                            <th>Période</th>
                            <th className="text-right">Budget</th>
                            <th>Statut</th>
                            <th className="text-center">Docs</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="8" className="text-center py-4">Chargement...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan="8" className="text-center text-muted py-4">Aucun projet</td></tr>
                        ) : filtered.map((p) => {
                            const st = findStatut(p.statut);
                            return (
                                <tr key={p.id}>
                                    <td>
                                        <a href="#!" onClick={(e) => { e.preventDefault(); openGallery(p); }}>
                                            <i className="fas fa-hard-hat text-warning mr-2"></i>{p.nom}
                                        </a>
                                        {p.description && <div className="small text-muted text-truncate projets-desc">{p.description}</div>}
                                    </td>
                                    <td>{p.clientName || "—"}</td>
                                    <td>{p.lieu || "—"}</td>
                                    <td className="small text-nowrap">{formatDate(p.dateDebut)} → {formatDate(p.dateFin)}</td>
                                    <td className="text-right text-nowrap">{formatAmount(p.budget)}</td>
                                    <td><span className={`badge ${st.css}`}>{st.label}</span></td>
                                    <td className="text-center"><span className="badge badge-light">{p.nbDocuments ?? 0}</span></td>
                                    <td className="text-right text-nowrap">
                                        <button className="btn btn-sm btn-outline-info mr-1" title="Galerie" onClick={() => openGallery(p)}>
                                            <i className="far fa-images"></i>
                                        </button>
                                        <button className="btn btn-sm btn-outline-secondary mr-1" title="Modifier" onClick={() => openEdit(p)}>
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => handleDelete(p)}>
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ---- Modal création / modification ---- */}
            <div className="modal fade" id="projetModal">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5><i className="fas fa-hard-hat mr-2"></i>{editing ? "Modifier le projet" : "Nouveau projet"}</h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group col-md-6">{renderField(formik, "nom", "Nom *", { type: "text" })}</div>
                                <div className="form-group col-md-6">
                                    <label>Client</label>
                                    <select name="clientId" className="form-control" value={formik.values.clientId} onChange={formik.handleChange}>
                                        <option value="">— Aucun —</option>
                                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-6">{renderField(formik, "lieu", "Lieu", { type: "text", placeholder: "ex : Rabat, Souissi" })}</div>
                                <div className="form-group col-md-3">{renderField(formik, "budget", "Budget (DH)", { type: "number", step: "0.01", min: "0" })}</div>
                                <div className="form-group col-md-3">
                                    <label>Statut</label>
                                    <select name="statut" className="form-control" value={formik.values.statut} onChange={formik.handleChange}>
                                        {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-6">{renderField(formik, "dateDebut", "Date de début", { type: "date" })}</div>
                                <div className="form-group col-md-6">{renderField(formik, "dateFin", "Date de fin", { type: "date" })}</div>
                            </div>
                            <div className="form-group mb-0">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    rows={4}
                                    className="form-control"
                                    placeholder="Travaux réalisés, matériaux, particularités…"
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button type="button" className="btn btn-enregistrer" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
                                Sauvegarder
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Modal galerie ---- */}
            <div className="modal fade" id="galleryModal">
                <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>
                                <i className="far fa-images mr-2"></i>{galleryProjet?.nom}
                                {galleryProjet && (
                                    <small className="text-muted d-block mt-1">
                                        {[galleryProjet.clientName, galleryProjet.lieu].filter(Boolean).join(" · ")}
                                        {" · "}{formatDate(galleryProjet.dateDebut)} → {formatDate(galleryProjet.dateFin)}
                                    </small>
                                )}
                            </h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            {galleryLoading ? (
                                <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>
                            ) : galleryDocs.length === 0 ? (
                                <div className="text-center text-muted py-5">
                                    <i className="far fa-images fa-3x mb-3 d-block"></i>
                                    Aucun document. Rattachez des photos / vidéos depuis Documents → ⋮ → « Rattacher à un projet ».
                                </div>
                            ) : (
                                <>
                                    {mediaDocs.length > 0 && (
                                        <div className="media-grid">
                                            {mediaDocs.map((d) => {
                                                const kind = mediaKind(d);
                                                const isCover = galleryProjet?.coverDocumentId === d.id;
                                                return (
                                                    <div key={d.id} className="media-tile" onClick={() => openLightbox(d)} title={d.commentaire || d.fileName}>
                                                        {kind === "image" && thumbs[d.id] ? (
                                                            <img src={thumbs[d.id]} alt={d.fileName} loading="lazy" />
                                                        ) : (
                                                            <div className="media-placeholder">
                                                                <i className={`fas ${kind === "video" ? "fa-play-circle" : "fa-image"} fa-3x`}></i>
                                                            </div>
                                                        )}
                                                        {kind === "video" && <span className="media-badge"><i className="fas fa-video"></i></span>}
                                                        {isCover && <span className="media-cover-badge"><i className="fas fa-star"></i> Couverture</span>}
                                                        <div className="media-caption">{d.commentaire || d.fileName}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {otherDocs.length > 0 && (
                                        <>
                                            <h6 className="text-muted mt-3">Autres documents</h6>
                                            <ul className="list-group">
                                                {otherDocs.map((d) => (
                                                    <li key={d.id} className="list-group-item d-flex align-items-center">
                                                        <i className="fas fa-file-alt text-secondary mr-2"></i>
                                                        <span className="flex-grow-1 text-truncate">
                                                            {d.fileName}
                                                            {d.commentaire && <small className="d-block text-muted">{d.commentaire}</small>}
                                                        </span>
                                                        <button className="btn btn-sm btn-outline-primary" onClick={() => handleDownload(d)}>
                                                            <i className="fas fa-download"></i>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Visionneuse photo / vidéo ---- */}
            {lightbox && (
                <div className="media-lightbox" onClick={closeLightbox}>
                    <div className="media-lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
                        <span className="text-truncate mr-3">
                            {lightbox.doc.fileName}
                            {lightbox.doc.commentaire && <small className="d-block text-white-50">{lightbox.doc.commentaire}</small>}
                        </span>
                        <div className="text-nowrap">
                            {lightbox.kind === "image" && (
                                <button className="btn btn-sm btn-light mr-2" onClick={() => setCover(lightbox.doc)}>
                                    <i className="fas fa-star mr-1"></i>Couverture
                                </button>
                            )}
                            <button className="btn btn-sm btn-light mr-2" onClick={() => handleDownload(lightbox.doc)}>
                                <i className="fas fa-download"></i>
                            </button>
                            <button className="btn btn-sm btn-light" onClick={closeLightbox}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                    {mediaDocs.length > 1 && (
                        <>
                            <button className="media-nav media-nav-left" onClick={(e) => { e.stopPropagation(); moveLightbox(-1); }}>
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            <button className="media-nav media-nav-right" onClick={(e) => { e.stopPropagation(); moveLightbox(1); }}>
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </>
                    )}
                    <div className="media-lightbox-content" onClick={(e) => e.stopPropagation()}>
                        {lightbox.loading || !lightbox.url ? (
                            <i className="fas fa-spinner fa-spin fa-3x text-white"></i>
                        ) : lightbox.kind === "video" ? (
                            <video src={lightbox.url} controls autoPlay />
                        ) : (
                            <img src={lightbox.url} alt={lightbox.doc.fileName} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Projets;
